import { type CSSProperties, useMemo } from 'react';

import type { JSONContent } from '@tiptap/core';
import classnames from 'classnames';
import type { EmblaOptionsType } from 'embla-carousel';

import EmblaCarousel from '../components/EmblaCarousel';
import Serializer from './components/Serializer';

import { computeFont } from '../utils/fonts';
import { useMobileCarouselSettings } from '../utils/useMobileCarouselSettings';
import { defaultStructure as defaultTestimonialsStructure } from './utils/defaultStructure';

import { BOX_SHADOW_VISIBILITY_MARGIN } from '../constants';
import type { SerializableNode } from '../serializer';
import { TestimonialsSerializer } from './Serializer';
import * as styles from './Testimonials.css';
import type { TestimonialsElementProps } from './Testimonials.types';

const COLUMNS_MAP: Record<string, string> = {
  '1': 'grid-cols-1',
  '2': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2',
  '3': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  '4': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
};

const OPTIONS: EmblaOptionsType = {
  align: 'start',
  containScroll: 'trimSnaps',
};

export function TestimonialsElement(props: TestimonialsElementProps) {
  const { element, children, attributes, className, isLive = false, viewport } = props;
  const {
    mobile_hide,
    desktop_hide,
    customAttributes,
    borderWidth,
    borderRadius,
    borderColor,
    borderStyle,
    padding,
    gap,
    mobileGap,
    shadow,
    backgroundColor,
    nameColor,
    descriptionColor,
    messageColor,
    nameFontFamily,
    descriptionFontFamily,
    messageFontFamily,
    data = [],
    alignment,
    cardStructureV2,
    structureTiptapState,
  } = element.attrs || {};

  const isViewportMobile = viewport === 'mobile' || element.attrs?.isViewportMobile || false;
  const mobileSliderControlsBackgroundColor = element.attrs?.mobileSliderControlsBackgroundColor;
  const mobileSliderControlsTextColor = element.attrs?.mobileSliderControlsTextColor;
  const mobileSliderControlsBorderColor = element.attrs?.mobileSliderControlsBorderColor;

  const {
    columns: effectiveColumns,
    shouldScrollHorizontally: displayedShouldScrollHorizontally,
    sliderControlsDisplayType: displayedSliderControlsDisplayType,
    sliderControlsBackgroundColor: displayedSliderControlsBackgroundColor,
    sliderControlsTextColor: displayedSliderControlsTextColor,
    sliderControlsBorderColor: displayedSliderControlsBorderColor,
  } = useMobileCarouselSettings({
    attributes: element.attrs,
    isViewportMobile: isViewportMobile || false,
  });

  const desktopSlideSize =
    displayedShouldScrollHorizontally && effectiveColumns ? `${100 / Number.parseInt(effectiveColumns, 10)}%` : '';
  const structureNode = useMemo(
    () =>
      (structureTiptapState?.content?.[0] || defaultTestimonialsStructure.content[0]) as JSONContent & SerializableNode,
    [structureTiptapState],
  );
  const cardMarginBottom = useMemo(() => {
    if (!displayedShouldScrollHorizontally || !shadow) {
      return undefined;
    }

    return BOX_SHADOW_VISIBILITY_MARGIN[shadow];
  }, [displayedShouldScrollHorizontally, shadow]);
  const testimonialCardStyle = {
    borderWidth,
    borderStyle,
    borderColor,
    borderRadius,
    boxShadow: shadow,
    padding,
  } as CSSProperties;
  const containerStyle = {
    width: '100%',

    '--gap': gap,
    '--mobile-gap': mobileGap || gap,
    '--padding': padding,
    '--background-color': backgroundColor,
    '--name-text-color': nameColor,
    '--description-text-color': descriptionColor,
    '--message-text-color': messageColor,
    '--name-font-family': computeFont(nameFontFamily) || 'inherit',
    '--description-font-family': computeFont(descriptionFontFamily) || 'inherit',
    '--message-font-family': computeFont(messageFontFamily) || 'inherit',
    '--alignment': alignment,

    '--testimonials-desktop-slide-size': desktopSlideSize,
    '--testimonials-slider-control-background-color': displayedSliderControlsBackgroundColor,
    '--testimonials-slider-control-text-color': displayedSliderControlsTextColor,
    '--testimonials-slider-control-border-color': displayedSliderControlsBorderColor,
    '--testimonials-mobile-slider-control-background-color':
      mobileSliderControlsBackgroundColor || displayedSliderControlsBackgroundColor,
    '--testimonials-mobile-slider-control-text-color':
      mobileSliderControlsTextColor || displayedSliderControlsTextColor,
    '--testimonials-mobile-slider-control-border-color':
      mobileSliderControlsBorderColor || displayedSliderControlsBorderColor,
  } as CSSProperties;

  return (
    <div {...customAttributes} className="relative flex flex-col" style={containerStyle} {...attributes}>
      <div
        className={classnames(
          className,
          {
            'mobile:hidden': mobile_hide,
            'desktop:hidden': desktop_hide,
          },
          customAttributes?.className,
          'relative w-full',
          styles.testimonialsGrid,
          displayedShouldScrollHorizontally ? 'flex' : 'grid',
          !displayedShouldScrollHorizontally && COLUMNS_MAP[effectiveColumns || '3'],
        )}
      >
        <EmblaCarousel
          options={OPTIONS}
          isCarousel={!!displayedShouldScrollHorizontally}
          isLive={isLive}
          controlsDisplayType={displayedSliderControlsDisplayType}
        >
          {data?.map((testimonial) => (
            <div key={testimonial.id} className="embla__slide relative" style={{ marginBottom: cardMarginBottom }}>
              {cardStructureV2 ? (
                <TestimonialsSerializer
                  node={structureNode}
                  rootClassName="h-full"
                  rootStyle={{}}
                  testimonial={testimonial}
                />
              ) : (
                <div style={testimonialCardStyle} className={classnames(styles.testimonialsElement)}>
                  <Serializer testimonial={testimonial} attributes={element.attrs} />
                </div>
              )}
            </div>
          ))}
        </EmblaCarousel>
      </div>

      {children}
    </div>
  );
}
